import {gsap, ScrollSmoother, ScrollTrigger, forceScrollSmootherUpdate, smoother} from "../utility/gsap";
import Dom from '../utility/dom';

const body = Dom.body;

const InitializeAnimatedCollage = () => {
  if(Dom.prefersMotion){
      const elements = document.querySelectorAll('.animated_collage');

      if(elements){
        elements.forEach(el => {
          el.animatedCollage = new TDAnimatedCollage(el);
        });
      }
  }
}

class TDAnimatedCollage {
  constructor(el){
    this.el = el;
    this.mainFigure = this.el.querySelector('.animated_collage__main');
    this.mainMediaEl = this.mainFigure.querySelector('.animated_collage__main_media');
    this.secondaryEls = this.el.querySelectorAll('.animated_collage__secondary');

    this.collageInitiated = false;
    this.parallaxIntiated = false;
    this.scrollTriggerOnScreen = false;

    this.determineLayout();
    this.resizeEvents();
  }


  determineLayout(){
    // Only apply animations if prefers motion.
    if(Dom.prefersMotion){
      // Set up collage animations.
      if((body.getBoundingClientRect().width >= 991 && !Dom.is_iOs) && !this.collageInitiated){
        this.layout = 'collage';
        this.collageInitiated = true;
        this.parallaxIntiated = false;
        
        if(!this.el.classList.contains('layout_collage')){
          this.el.classList.add('layout_collage');
          this.el.classList.remove('layout_parallax');
        }

        this.switchLayout();
      }
      // Set up parallax animations.
      else if((Dom.body.clientWidth < 991 || Dom.is_iOs) && !this.parallaxInitiated){
        this.layout = 'parallax';
        this.parallaxIntiated = true;
        this.collageInitiated = false;

        if(!this.el.classList.contains('layout_parallax')){
          this.el.classList.add('layout_parallax');
          this.el.classList.remove('layout_collage');
        }

        this.switchLayout();
      }
    }
    else {
      this.layout = 'static';
      
      if(!this.el.classList.contains('layout_static')){
        this.el.classList.add('layout_static');
      }
    }
  }


  switchLayout() {
    this.resetTransforms();

    if(this.layout == 'parallax'){
      if(this.collageTL && this.collageTL.scrollTrigger){
        this.collageTL.scrollTrigger.disable();
      }
      this.initParallaxGSAP();
    }
    else if(this.layout == 'collage'){
      if(this.parallaxTL && this.parallaxTL.scrollTrigger){
        this.parallaxTL.scrollTrigger.disable();
      }

      this.initCollageGSAP();
    }
  }


  initCollageGSAP() {
    if(!this.collageTL){
      this.collageTL = gsap.timeline({
        scrollTrigger: {
          trigger: this.el,
          scrub:true,
          pin:true,
          invalidateOnRefresh: true,
          start: "top top",
          end: () => {
            return "+=" + this.getTotalScrollAmount();
          },
          onUpdate : self => {
            this.el.style.setProperty('--progress', self.progress.toFixed(3));
            this.el.style.setProperty('--progress-remaining', (1 - self.progress).toFixed(3));
          },
          onToggle: (self) => {
            if(self.isActive){
              this.scrollTriggerOnScreen = true;
              this.el.classList.remove('collage_complete')
            }
            else {
              this.scrollTriggerOnScreen = false;
              this.el.classList.add('collage_complete');
            }
          }
        }
      });

      // Animate the main element
      this.collageTL.fromTo(
        this.mainMediaEl,
        {
          scale: () => {
            return getMainStartScale(this.mainFigure);
          }
        }, 
        {
          scaleX: 1,
          scaleY: 1
        },
        0
      );
      
      // Animate Secondary Images in
      if(this.secondaryEls){
        this.secondaryEls.forEach((figure, i) => {
          const img = figure.querySelector('img');
          this.collageTL.fromTo(
            img,
            {
              scale: () => {
                return this.getSecondaryScaleStart(i);
              },
              x: () => {
                return this.getSecondaryCollageStartPos(i).x;
              },
              y: () => {
                return this.getSecondaryCollageStartPos(i).y;
              },
              xPercent: () => {
                return this.getSecondaryCollageStartPos(i).xPercent;
              },
              yPercent: () => {
                return this.getSecondaryCollageStartPos(i).yPercent;
              }
            }, 
            {
              scale: 1,
              x: 0,
              y: 0,
              xPercent: 0,
              yPercent: 0
            },
            0
          );
        });
      }
    }
    else {
      this.collageTL.scrollTrigger.enable();
    }
  }

  
  getSecondaryScaleStart(i) {
    let scale = 3; // Default

    if(i == 0){
      scale = 2;
    }

    return scale;
  }


  getSecondaryCollageStartPos(i){
    // Set defaults
    let x = 0;
    let y = 0;
    let xPercent = 0;
    let yPercent = 0;

    // Get data for the main figure element wrap and this image for comparison.
    const mainData = this.mainFigure.getBoundingClientRect();
    const imgData = this.secondaryEls[i].getBoundingClientRect();

    if(i == 0){
      x = (imgData.left * -1) - 50;
      y = window.innerHeight * -0.25;
      xPercent = -100;
    }

    if(i == 1){
      x = (imgData.left / 3);
      y = (imgData.top * -1) - 100;
      yPercent = -100;
    }

    if(i == 2){
      x = (mainData.width * -0.5);
      y = mainData.top * 3;
      yPercent = 100;
    }

    if(i == 3){
      x = mainData.left;
      y = imgData.height / 3;
      xPercent = 100;
    }

    if(i == 4){
      x = mainData.width * -0.15;
      y = mainData.top + 200;
      yPercent = 100;
    }

    const position = {'x' : x, 'y' : y, 'xPercent': xPercent, 'yPercent' : yPercent};

    return position;
  }


  initParallaxGSAP() {
    if(!this.parallaxTL){
      this.parallaxTL = gsap.timeline({
        scrollTrigger: {
          trigger: this.el,
          scrub:true,
          invalidateOnRefresh: true,
          start: () => {
            // Since some parallax images have negative top positions, account for them.
            const offset = this.el.getBoundingClientRect().height * 0.3;
            return "top bottom+=" + offset;
          },
          end: "bottom top",
          onUpdate : self => {
            this.el.style.setProperty('--progress', self.progress.toFixed(3));
            this.el.style.setProperty('--progress-remaining', (1 - self.progress).toFixed(3));
          },
          onToggle: (self) => {
            if(self.isActive){
              this.scrollTriggerOnScreen = true;
            }
            else {
              this.scrollTriggerOnScreen = false;
            }
          }
        }
      });
      
      // Animate Secondary Image Parallax Speed
      if(this.secondaryEls){
        this.secondaryEls.forEach((figure, i) => {
          const img = figure.querySelector('img');

          gsap.set(img, {y: this.getSecondaryParallaxStartPos(i)});

          this.parallaxTL.fromTo(
            img,
            {
              y: () => {
                return this.getSecondaryParallaxStartPos(i);
              },
              x:0,
              scale:1
            }, 
            {
              y: () => {
                return 0
              },
              x: 0,
              scale:1
            },
            0
          );
        });
      }
    }
    else {
      this.parallaxTL.scrollTrigger.enable();
    }
  }


  getSecondaryParallaxStartPos(i) {
    const windowHeight = window.innerHeight;

    if(i == 0){
      return windowHeight * .2;
    }
    else if(i == 1){
      return windowHeight * 0.9;
    }
    else if(i == 2){
      return windowHeight * .6;
    }
    else if(i == 3){
      return windowHeight * 1.4;
    }
    else if(i == 4){
      return windowHeight * .7
    }
  }


  getTotalScrollAmount(){
    const minScroll = 600;
    const maxScroll = 1200;
    const windowWidth = window.innerWidth;
    let scroll;
  
    if(windowWidth < maxScroll && windowWidth > minScroll){
      scroll = windowWidth;
    }
    else if(windowWidth > maxScroll){
      scroll = maxScroll;
    }
    else {
      scroll = minScroll;
    }
    
    return scroll + 'px';
  }


  resetTransforms() {
    gsap.set(this.mainMediaEl, {scaleX:1, scaleY:1, x:0, y:0, xPercent: 0, yPercent: 0 });
    if(this.secondaryEls){
      this.secondaryEls.forEach(figure => {
        gsap.set(figure.querySelector('img'), {scaleX:1, scaleY:1, x:0, y:0, xPercent: 0, yPercent: 0 });
      });
    }
  }


  repositionPinnedElement(){
    const pinContainer = this.el.closest('.pin-spacer');
    const pinData = pinContainer.getBoundingClientRect();
    
    if((pinData.top + pinData.height) <  window.innerHeight) {
      const pinBottom = pinContainer.style.paddingBottom;
      gsap.set(this.el, {y: pinBottom});
    }
  }


  resizeEvents(){
    let timeout = null;

    window.addEventListener('resize', () => {
      if(timeout){
        clearTimeout(timeout);
      }

      // Put resize events within timeout for debouncing.
      timeout = setTimeout(()=>{
        this.determineLayout();

        // If we're passed the end of the pin container and the window is resized
        // The element may go back to the top of the container. We'll fix that manually
        if(this.layout == 'collage' && this.collageTL){
          this.repositionPinnedElement();
        }
        
        timeout = false;
      }, 300);
    });
  }
}

const getMainStartScale = (mainFigure) => {
  const figureWidth = mainFigure.getBoundingClientRect().width;
  const windowWidth = window.innerWidth;
  const scale = (windowWidth / figureWidth).toFixed(2);

  return scale
}

export { InitializeAnimatedCollage }